import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import { 
  Users, 
  TrendingUp, 
  Copy, 
  Activity, 
  Star, 
  Crown,
  Award,
  Medal,
  ChevronRight,
  UserPlus,
  UserMinus,
  Settings,
  BarChart2,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Filter,
  RefreshCw,
  Trophy,
  Target,
  Zap,
  Eye,
  X,
  Check,
  Percent
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SocialTradingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [feed, setFeed] = useState([]);
  const [activeCopies, setActiveCopies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyConfig, setCopyConfig] = useState({
    allocation_percent: 10,
    max_trade_size: 100,
    copy_stocks: true,
    copy_crypto: true,
    copy_metals: true
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'leaderboard') {
        const res = await api.get('/social/leaderboard?limit=50');
        setLeaderboard(res.data.leaderboard || []);
      } else if (activeTab === 'following') {
        const res = await api.get('/social/following');
        setFollowing(res.data.following || []);
      } else if (activeTab === 'followers') {
        const res = await api.get('/social/followers');
        setFollowers(res.data.followers || []);
      } else if (activeTab === 'feed') {
        const res = await api.get('/social/feed?limit=50');
        setFeed(res.data.feed || []);
      } else if (activeTab === 'copying') {
        const res = await api.get('/social/copy-trade/active');
        setActiveCopies(res.data.active_copies || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (traderId) => {
    try {
      await api.post('/social/follow', { user_id: traderId });
      toast.success('Now following trader!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to follow');
    }
  };

  const handleUnfollow = async (traderId) => {
    try {
      await api.post('/social/unfollow', { user_id: traderId });
      toast.success('Unfollowed trader');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfollow');
    }
  };

  const handleStartCopyTrade = async () => {
    if (!selectedTrader) return;
    try {
      await api.post('/social/copy-trade/start', {
        trader_id: selectedTrader.user_id,
        ...copyConfig
      });
      toast.success(`Now copying ${selectedTrader.name}'s trades!`);
      setShowCopyModal(false);
      setSelectedTrader(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start copy trading');
    }
  };

  const handleStopCopyTrade = async (traderId) => {
    try {
      await api.post('/social/copy-trade/stop', { trader_id: traderId });
      toast.success('Stopped copy trading');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to stop copy trading');
    }
  };

  const openCopyModal = (trader) => {
    setSelectedTrader(trader);
    setShowCopyModal(true);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Award className="w-5 h-5 text-orange-400" />;
    return <span className="text-gray-500 font-medium">#{rank}</span>;
  };

  const filteredLeaderboard = leaderboard.filter(trader => 
    trader.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    trader.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="bg-[#111111] border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#00E676] to-[#00C853] rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-black" />
              </div>
              <span className="text-xl font-bold text-white">Sraivest</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => fetchData()}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <Link 
                to="/dashboard" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#00E676]/10 rounded-lg">
              <Users className="w-6 h-6 text-[#00E676]" />
            </div>
            <h1 className="text-3xl font-bold text-white">Social Trading</h1>
            <span className="px-2 py-1 bg-[#00E676] text-black text-xs font-bold rounded-full">
              LIVE
            </span>
          </div>
          <p className="text-gray-400">
            Follow top traders, copy their strategies, and grow together
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#1A1A1A] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-[#00E676]" />
              <span className="text-gray-400 text-sm">Following</span>
            </div>
            <p className="text-2xl font-bold text-white">{user?.following_count || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-gray-400 text-sm">Followers</span>
            </div>
            <p className="text-2xl font-bold text-white">{user?.followers_count || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Copy className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400 text-sm">Copying</span>
            </div>
            <p className="text-2xl font-bold text-white">{activeCopies.length}</p>
          </div>
          <div className="bg-[#1A1A1A] rounded-xl p-4 border border-gray-800">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="w-4 h-4 text-orange-400" />
              <span className="text-gray-400 text-sm">Your Rank</span>
            </div>
            <p className="text-2xl font-bold text-white">
              {leaderboard.find(t => t.user_id === user?.user_id)?.rank || '-'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
            { id: 'following', label: 'Following', icon: Users },
            { id: 'followers', label: 'Followers', icon: Star },
            { id: 'copying', label: 'Copy Trading', icon: Copy },
            { id: 'feed', label: 'Activity Feed', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#00E676] text-black'
                  : 'bg-[#1A1A1A] text-gray-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search (for leaderboard) */}
        {activeTab === 'leaderboard' && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search traders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#1A1A1A] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#00E676]"
              data-testid="search-traders"
            />
          </div>
        )}

        {/* Content */}
        <div className="bg-[#1A1A1A] rounded-xl border border-gray-800 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Leaderboard Tab */}
              {activeTab === 'leaderboard' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#111111]">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Rank</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Trader</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Win Rate</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Total Profit</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Trades</th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Followers</th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {filteredLeaderboard.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                            No traders found. Be the first to trade!
                          </td>
                        </tr>
                      ) : (
                        filteredLeaderboard.map((trader) => (
                          <tr key={trader.user_id} className="hover:bg-[#222222] transition-colors" data-testid={`trader-row-${trader.rank}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-center w-8 h-8">
                                {getRankIcon(trader.rank)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-[#00E676] to-[#00C853] rounded-full flex items-center justify-center text-black font-bold">
                                  {trader.name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <div>
                                  <p className="text-white font-medium">{trader.name || 'Anonymous'}</p>
                                  <p className="text-gray-500 text-sm">{trader.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-[#00E676] rounded-full"
                                    style={{ width: `${trader.stats?.win_rate || 0}%` }}
                                  />
                                </div>
                                <span className="text-white font-medium">{trader.stats?.win_rate || 0}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`font-medium ${(trader.stats?.total_profit || 0) >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                                {(trader.stats?.total_profit || 0) >= 0 ? '+' : ''}${(trader.stats?.total_profit || 0).toFixed(2)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-white">
                              {trader.stats?.total_trades || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-white">
                              {trader.followers_count || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-2">
                                {trader.user_id !== user?.user_id && (
                                  <>
                                    {trader.is_following ? (
                                      <button
                                        onClick={() => handleUnfollow(trader.user_id)}
                                        className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                                        title="Unfollow"
                                        data-testid={`unfollow-${trader.rank}`}
                                      >
                                        <UserMinus className="w-4 h-4 text-gray-400" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleFollow(trader.user_id)}
                                        className="p-2 bg-[#00E676]/10 hover:bg-[#00E676]/20 rounded-lg transition-colors"
                                        title="Follow"
                                        data-testid={`follow-${trader.rank}`}
                                      >
                                        <UserPlus className="w-4 h-4 text-[#00E676]" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => openCopyModal(trader)}
                                      className="p-2 bg-purple-500/10 hover:bg-purple-500/20 rounded-lg transition-colors"
                                      title="Copy Trades"
                                      data-testid={`copy-${trader.rank}`}
                                    >
                                      <Copy className="w-4 h-4 text-purple-400" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Following Tab */}
              {activeTab === 'following' && (
                <div className="p-6">
                  {following.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 mb-4">You're not following anyone yet</p>
                      <button
                        onClick={() => setActiveTab('leaderboard')}
                        className="px-4 py-2 bg-[#00E676] text-black rounded-lg font-medium hover:bg-[#00C853] transition-colors"
                      >
                        Discover Traders
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {following.map((trader) => (
                        <TraderCard
                          key={trader.user_id}
                          trader={trader}
                          onFollow={() => handleFollow(trader.user_id)}
                          onUnfollow={() => handleUnfollow(trader.user_id)}
                          onCopy={() => openCopyModal(trader)}
                          isFollowing={true}
                          currentUserId={user?.user_id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Followers Tab */}
              {activeTab === 'followers' && (
                <div className="p-6">
                  {followers.length === 0 ? (
                    <div className="text-center py-12">
                      <Star className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No followers yet. Keep trading to attract followers!</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {followers.map((follower) => (
                        <div key={follower.user_id} className="bg-[#111111] rounded-xl p-4 border border-gray-700">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-black font-bold">
                              {follower.name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <p className="text-white font-medium">{follower.name || 'Anonymous'}</p>
                              <p className="text-gray-500 text-sm">Followed {new Date(follower.followed_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Copy Trading Tab */}
              {activeTab === 'copying' && (
                <div className="p-6">
                  {activeCopies.length === 0 ? (
                    <div className="text-center py-12">
                      <Copy className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 mb-4">You're not copying any traders</p>
                      <button
                        onClick={() => setActiveTab('leaderboard')}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors"
                      >
                        Find Traders to Copy
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activeCopies.map((copy) => (
                        <div key={copy.copy_id} className="bg-[#111111] rounded-xl p-6 border border-gray-700">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                                {copy.trader?.name?.[0]?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="text-white font-medium">{copy.trader?.name || 'Anonymous'}</p>
                                <p className="text-purple-400 text-sm">Copying since {new Date(copy.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleStopCopyTrade(copy.trader?.user_id)}
                              className="px-3 py-1 bg-red-500/10 text-red-500 rounded-lg text-sm hover:bg-red-500/20 transition-colors"
                              data-testid={`stop-copy-${copy.copy_id}`}
                            >
                              Stop Copying
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-gray-500 text-sm">Allocation</p>
                              <p className="text-white font-medium">{copy.config?.allocation_percent}%</p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-sm">Max Trade</p>
                              <p className="text-white font-medium">${copy.config?.max_trade_size}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-sm">Copied Trades</p>
                              <p className="text-white font-medium">{copy.stats?.total_copied_trades || 0}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-sm">Trader Win Rate</p>
                              <p className="text-[#00E676] font-medium">{copy.trader_stats?.win_rate || 0}%</p>
                            </div>
                          </div>
                          
                          <div className="mt-4 flex gap-2">
                            {copy.config?.copy_stocks && (
                              <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded">Stocks</span>
                            )}
                            {copy.config?.copy_crypto && (
                              <span className="px-2 py-1 bg-orange-500/10 text-orange-400 text-xs rounded">Crypto</span>
                            )}
                            {copy.config?.copy_metals && (
                              <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded">Metals</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Activity Feed Tab */}
              {activeTab === 'feed' && (
                <div className="p-6">
                  {feed.length === 0 ? (
                    <div className="text-center py-12">
                      <Activity className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No activity yet. Follow traders to see their activity!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {feed.map((entry, idx) => (
                        <FeedEntry key={entry.feed_id || idx} entry={entry} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Copy Trade Modal */}
      {showCopyModal && selectedTrader && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                    {selectedTrader.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Copy {selectedTrader.name}</h3>
                    <p className="text-gray-400 text-sm">Configure copy trading settings</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCopyModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Trader Stats */}
              <div className="grid grid-cols-3 gap-4 bg-[#111111] rounded-xl p-4">
                <div className="text-center">
                  <p className="text-gray-500 text-xs">Win Rate</p>
                  <p className="text-[#00E676] font-bold">{selectedTrader.stats?.win_rate || 0}%</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 text-xs">Total Profit</p>
                  <p className="text-white font-bold">${(selectedTrader.stats?.total_profit || 0).toFixed(2)}</p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500 text-xs">Total Trades</p>
                  <p className="text-white font-bold">{selectedTrader.stats?.total_trades || 0}</p>
                </div>
              </div>

              {/* Allocation */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  Portfolio Allocation: {copyConfig.allocation_percent}%
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={copyConfig.allocation_percent}
                  onChange={(e) => setCopyConfig({...copyConfig, allocation_percent: parseInt(e.target.value)})}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#00E676]"
                  data-testid="allocation-slider"
                />
                <p className="text-gray-500 text-xs mt-1">% of your balance used per copied trade</p>
              </div>

              {/* Max Trade Size */}
              <div>
                <label className="block text-gray-400 text-sm mb-2">Max Trade Size ($)</label>
                <input
                  type="number"
                  min="5"
                  max="1000"
                  value={copyConfig.max_trade_size}
                  onChange={(e) => setCopyConfig({...copyConfig, max_trade_size: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 bg-[#111111] border border-gray-700 rounded-xl text-white focus:outline-none focus:border-[#00E676]"
                  data-testid="max-trade-input"
                />
              </div>

              {/* Asset Types */}
              <div>
                <label className="block text-gray-400 text-sm mb-3">Copy Asset Types</label>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: 'copy_stocks', label: 'Stocks', color: 'blue' },
                    { key: 'copy_crypto', label: 'Crypto', color: 'orange' },
                    { key: 'copy_metals', label: 'Metals', color: 'yellow' }
                  ].map(asset => (
                    <button
                      key={asset.key}
                      onClick={() => setCopyConfig({...copyConfig, [asset.key]: !copyConfig[asset.key]})}
                      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        copyConfig[asset.key]
                          ? `bg-${asset.color}-500/20 text-${asset.color}-400 border border-${asset.color}-500/50`
                          : 'bg-gray-700 text-gray-400 border border-gray-600'
                      }`}
                      data-testid={`toggle-${asset.key}`}
                    >
                      {copyConfig[asset.key] ? <Check className="w-4 h-4" /> : null}
                      {asset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-700">
              <button
                onClick={handleStartCopyTrade}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                data-testid="start-copy-btn"
              >
                <Copy className="w-5 h-5" />
                Start Copy Trading
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Trader Card Component
function TraderCard({ trader, onFollow, onUnfollow, onCopy, isFollowing, currentUserId }) {
  return (
    <div className="bg-[#111111] rounded-xl p-4 border border-gray-700 hover:border-gray-600 transition-colors">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-[#00E676] to-[#00C853] rounded-full flex items-center justify-center text-black font-bold">
          {trader.name?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{trader.name || 'Anonymous'}</p>
          <p className="text-gray-500 text-sm truncate">{trader.email}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-gray-500 text-xs">Win Rate</p>
          <p className="text-[#00E676] font-medium">{trader.stats?.win_rate || 0}%</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Total Profit</p>
          <p className={`font-medium ${(trader.stats?.total_profit || 0) >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
            ${(trader.stats?.total_profit || 0).toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Trades</p>
          <p className="text-white font-medium">{trader.stats?.total_trades || 0}</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs">Followers</p>
          <p className="text-white font-medium">{trader.followers_count || 0}</p>
        </div>
      </div>
      
      {trader.user_id !== currentUserId && (
        <div className="flex gap-2">
          {isFollowing ? (
            <button
              onClick={onUnfollow}
              className="flex-1 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
            >
              Unfollow
            </button>
          ) : (
            <button
              onClick={onFollow}
              className="flex-1 py-2 bg-[#00E676]/10 text-[#00E676] rounded-lg text-sm font-medium hover:bg-[#00E676]/20 transition-colors"
            >
              Follow
            </button>
          )}
          <button
            onClick={onCopy}
            className="flex-1 py-2 bg-purple-500/10 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/20 transition-colors"
          >
            Copy
          </button>
        </div>
      )}
    </div>
  );
}

// Feed Entry Component
function FeedEntry({ entry }) {
  const getActionIcon = () => {
    switch (entry.action_type) {
      case 'follow': return <UserPlus className="w-4 h-4 text-[#00E676]" />;
      case 'copy_start': return <Copy className="w-4 h-4 text-purple-400" />;
      case 'copy_trade': return <Zap className="w-4 h-4 text-yellow-400" />;
      case 'trade': return <TrendingUp className="w-4 h-4 text-blue-400" />;
      default: return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="flex items-start gap-4 bg-[#111111] rounded-xl p-4 border border-gray-700">
      <div className="w-10 h-10 bg-gradient-to-br from-[#00E676] to-[#00C853] rounded-full flex items-center justify-center text-black font-bold flex-shrink-0">
        {entry.user?.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-white font-medium">{entry.user?.name || 'Anonymous'}</span>
          {getActionIcon()}
          <span className="text-gray-400">{entry.message}</span>
        </div>
        <p className="text-gray-500 text-sm">
          {new Date(entry.created_at).toLocaleString()}
        </p>
        {entry.trade_data && (
          <div className="mt-2 px-3 py-2 bg-[#0A0A0A] rounded-lg inline-block">
            <span className="text-gray-400 text-sm">
              {entry.trade_data.asset} - ${entry.trade_data.amount?.toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
